import React from 'react';
import { useTranslation } from 'react-i18next';

import { businessUtcOffsetMinutes, formatDeviceLocalClock, formatUtcOffset, toBusinessDate } from 'config/time';

/** 記録の端末の時間帯の項目（API の device_time_zone・device_utc_offset_minutes）。 */
export interface DeviceTimeZoneValue {
  zone?: string | null;
  offset?: number | null;
}

/** 端末の時間帯が記録されているか（名前か差のどちらかがある）。 */
export function hasDeviceTimeZone(value: DeviceTimeZoneValue): boolean {
  return (typeof value.zone === 'string' && value.zone !== '') || (typeof value.offset === 'number' && Number.isFinite(value.offset));
}

/**
 * 退勤・作業の停止の側が、管理者が後から入れた時刻か（TZ-AC36、2026-10-11 PM 判断）。
 * アプリは出勤・開始と退勤・停止の両方で端末の時間帯を送る（段階3 から）。出勤・開始の側にあり、退勤・停止の側が
 * 空なら、退勤・停止はアプリの操作ではない（管理者の編集）とみなす。両方が空なら旧アプリの記録。
 * 修正の履歴（BE-09）には頼らない。限界は記録（ev116-stage3/README.md）に書いた。
 */
export function isAdminEnteredEnd(record: any, endPrefix: 'check_out_' | 'stop_'): boolean {
  return hasDeviceTimeZone(deviceTimeZoneOf(record)) && !hasDeviceTimeZone(deviceTimeZoneOf(record, endPrefix));
}

/** 記録から端末の時間帯を取り出す。prefix は勤怠の退勤 `check_out_`・作業の停止 `stop_`。 */
export function deviceTimeZoneOf(record: any, prefix = ''): DeviceTimeZoneValue {
  return {
    zone: record?.[`${prefix}device_time_zone`],
    offset: record?.[`${prefix}device_utc_offset_minutes`],
  };
}

interface DeviceTimeZoneProps extends DeviceTimeZoneValue {
  /** 記録の時刻（UTC の ISO 文字列）。基準（会社の時間帯）の差と比べ、端末の現地の時刻を求める。 */
  at?: Date | string | null;
  /** 1つの列に2つ並べるときの区別（既存の見出しの文言。出勤・退勤、開始・終了）。 */
  label?: string;
  /** 詳細の画面。名前を差と並べて見せる。 */
  detail?: boolean;
  /** 端末の時間帯が無いとき、管理者が後から入れた時刻として「管理者が入力」と出す（TZ-AC36。説明は出さない）。 */
  adminEntered?: boolean;
  className?: string;
}

/**
 * 記録ごとの「端末の時間帯」（EV-116 段階3、2026-10-11 PM の決定3・11）。
 *
 * - 差（`UTC-07:00`）を出す。名前（`America/Los_Angeles`）はツールチップと読み上げで、詳細では並べて出す。
 * - 端末の差が、その記録の時刻の集計の基準（会社の時間帯）の差と違うときだけ、印（`data-device-time-zone="differs"`
 *   と色）を付け、端末の現地の時刻（`MM/dd HH:mm`）を補足に出す。
 * - 端末の時間帯が無い記録（端末の時間帯を送る前のアプリ）は「記録なし」。推定の値は出さない。
 */
const DeviceTimeZone: React.FC<DeviceTimeZoneProps> = ({ zone, offset, at, label, detail = false, adminEntered = false, className = '' }) => {
  const { t, i18n } = useTranslation('common');
  // TZ-AC38（懸念4）。読み上げで部品が区切りなしで続かないよう、視覚的に隠した区切りを入れる（新しい文言ではない）。
  const separator = <span className="sr-only">{i18n.language === 'jp' ? '、' : ', '}</span>;
  const name = typeof zone === 'string' && zone !== '' ? zone : '';
  const hasOffset = typeof offset === 'number' && Number.isFinite(offset);
  const prefix = label ? (
    <>
      <span className="mr-1">{label}</span>
      {separator}
    </>
  ) : null;

  if (!hasOffset && !name && adminEntered) {
    return (
      <span data-device-time-zone="admin" className={`text-gray-500 ${className}`}>
        {prefix}
        <span>{t('time-zone.device-admin-entered')}</span>
      </span>
    );
  }

  if (!hasOffset && !name) {
    return (
      // TZ-AC33（2026-10-11 PM 承認）。説明はツールチップ（title）と、読み上げ用の視覚的に隠した文字の両方で出す。
      <span data-device-time-zone="none" title={t('time-zone.device-none-reason')} className={`text-gray-500 ${className}`}>
        {prefix}
        <span>{t('time-zone.device-none')}</span>
        {separator}
        <span className="sr-only">{t('time-zone.device-none-reason')}</span>
      </span>
    );
  }

  const differs = hasOffset && !!toBusinessDate(at) && offset !== businessUtcOffsetMinutes(at);
  return (
    <span
      data-device-time-zone={differs ? 'differs' : 'same'}
      title={name || undefined}
      className={`${differs ? 'text-amber-700 font-medium' : ''} ${className}`}
    >
      {prefix}
      <span className="whitespace-nowrap">{hasOffset ? formatUtcOffset(offset as number) : name}</span>
      {hasOffset && name && separator}
      {hasOffset && name && (detail ? <span className="ml-1">{name}</span> : <span className="sr-only">{name}</span>)}
      {/* TZ-AC34（2026-10-11 PM 承認）。前置き「端末では」と時刻を同じ補足の中に並べる（続けて読み上げられる）。 */}
      {differs && separator}
      {differs && (
        <span className="block text-xs">
          <span>{t('time-zone.device-local-prefix')}</span> <span>{formatDeviceLocalClock(at, offset as number)}</span>
        </span>
      )}
    </span>
  );
};

export default DeviceTimeZone;
